import { Injectable, inject } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { currentUsername } from './current-username';
import {
    ReturnPackage,
    normalizeCode,
    returnSearchKeys,
} from './return-package';
import { ReturnsApiService } from './returns-api.service';

// The shop's own record of which return packages have physically arrived.
// None of the platforms can be told "we received it" through the delegate
// today, and their own logistics statuses stop at the courier's word, so the
// confirmation lives here: one document per mark in its own collection
// (management-console.return_receipts, through the returns Lambda). Until
// 2026-09 all marks were one App Config Store document, rewritten whole on
// every save — two operators saving at once could drop each other's marks.

export interface ReceiptMark {
    // ISO datetime the operator confirmed receipt.
    at: string;
    // Cognito username of whoever confirmed, when known.
    by?: string;
    // What was typed or scanned to find the package, as entered.
    code?: string;
}

// Keyed two ways:
//   <storeId>:<returnId>   a return the tracker had listed when it was
//                          confirmed (ReturnPackage.id)
//   code:<normalized>      a code confirmed before any listed return matched
//                          it — a package that arrived ahead of the
//                          platform's record, or one outside the lookback.
//                          It attaches to a return as soon as one lists
//                          with that code (see receiptFor).
export interface ReturnReceiptsDoc {
    receipts: Record<string, ReceiptMark>;
}

export const codeReceiptKey = (code: unknown): string =>
    `code:${normalizeCode(code)}`;

// The confirmation on record for a listed return, whichever way it was
// keyed, or null when it has not been confirmed.
//
// A code-keyed mark attaches only to a return that was already open when
// the mark was made: the parcel was in hand at that moment, so it cannot be
// a return the buyer opened later on the same order. Without this, one
// "record anyway" by order number would pre-confirm every future return on
// that order.
//
// A failed delivery is exempt: its date is when the courier turned it
// around or got it back (the platforms record nothing earlier), so a parcel
// scanned at the counter minutes before the platform catches up would
// otherwise never attach — and no later return can follow on an order the
// buyer never received.
export const receiptFor = (
    row: ReturnPackage,
    doc: ReturnReceiptsDoc,
): ReceiptMark | null => {
    const direct = doc.receipts[row.id];
    if (direct) {
        return direct;
    }
    for (const key of returnSearchKeys(row)) {
        const byCode = doc.receipts[`code:${key}`];
        if (byCode && openedBefore(row, byCode)) {
            return byCode;
        }
    }
    return null;
};

const openedBefore = (row: ReturnPackage, mark: ReceiptMark): boolean => {
    if (!row.createdAt || row.source === 'failed_delivery') {
        return true;
    }
    const markedAt = moment(mark.at);
    return !markedAt.isValid() || !row.createdAt.isAfter(markedAt);
};

@Injectable({
    providedIn: 'root',
})
export class ReturnReceiptService {
    private api = inject(ReturnsApiService);

    // Every mark on record; empty until the first confirmation.
    getDoc(): Observable<ReturnReceiptsDoc> {
        return this.api.request<ReturnReceiptsDoc>('receipts');
    }

    // Records a confirmation under `key` (the server stamps the time) and
    // resolves to every mark on record, others' included.
    mark(key: string, code?: string): Observable<ReturnReceiptsDoc> {
        return currentUsername().pipe(
            switchMap((by) =>
                this.api.request<ReturnReceiptsDoc>('markReceipt', {
                    key,
                    ...(by ? { by } : {}),
                    ...(code ? { code } : {}),
                }),
            ),
        );
    }

    // Removes a confirmation. `keys` lists every key the mark may sit under
    // (the return's own id and its code keys), since receiptFor accepts any.
    unmark(keys: string[]): Observable<ReturnReceiptsDoc> {
        return this.api.request<ReturnReceiptsDoc>('unmarkReceipts', { keys });
    }
}
